// frontend/src/components/asset/MyInspectionsDue.js
/**
 * Driver-facing monthly self-inspection queue on /my-assets.
 *
 * Self-hides when nothing is due/overdue (same pattern as MyTrafficOffences
 * and MyAccidents). Sits above the stats and tabs on purpose: monthly
 * walk-arounds are an ops compliance obligation — a driver who never opens
 * a fleet asset card must still see and complete them.
 *
 * No exact due date is shown. The system tracks the last walk-around the
 * driver submitted and whether this calendar month is covered.
 *
 * Theme: Employee (blue), with amber/red reserved for overdue urgency.
 */

import React, { useState } from 'react';
import { Car, ClipboardCheck, CalendarClock, History } from 'lucide-react';
import { StatusBadge } from '../UI/StatusBadge';
import InspectVehicleModal from './InspectVehicleModal';
import { formatDate } from '../../utils/dateUtils';
import {
  useMyInspectionsDue,
  useSubmitVehicleInspection,
} from '../../hooks/useAssetInventoryData';

const Fact = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 text-xs text-gray-600">
    <Icon className="w-3 h-3 text-gray-400 flex-shrink-0" />
    {children}
  </span>
);

const lastDoneLabel = (row) => {
  if (!row?.last_inspection_date) return 'Never inspected';
  const days = row.days_since_last;
  const when = formatDate(row.last_inspection_date);
  if (days == null) return `Last done ${when}`;
  if (days === 0) return `Last done today (${when})`;
  if (days === 1) return `Last done yesterday (${when})`;
  return `Last done ${when} · ${days} days ago`;
};

const InspectionDueCard = ({ row, onInspect }) => {
  const isOverdue = row.row_status === 'overdue';

  return (
    <div
      className={`bg-white rounded-xl border p-4 space-y-3 ${
        isOverdue ? 'border-red-200' : 'border-amber-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{row.vehicle_number}</p>
          <p className="text-xs text-gray-400 truncate">
            {[row.brand, row.model].filter(Boolean).join(' ') || row.vehicle_type_display || 'Fleet vehicle'}
          </p>
        </div>
        <StatusBadge status={row.row_status} />
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <Fact icon={Car}>{row.vehicle_type_display || 'Fleet'}</Fact>
        <Fact icon={History}>{lastDoneLabel(row)}</Fact>
      </div>

      {isOverdue ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 flex items-center gap-1.5">
          <CalendarClock className="w-3.5 h-3.5 text-red-700 flex-shrink-0" />
          <span className="text-xs font-medium text-red-800">
            This month&apos;s vehicle inspection is overdue — complete it when you next see the vehicle
          </span>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          Once a month: check the vehicle, tick each checklist item, and submit.
          The system records when you submit — you do not pick a date. Flagged items notify the custodian.
        </p>
      )}

      <button
        type="button"
        onClick={() => onInspect(row)}
        className={isOverdue ? 'btn btn-danger w-full sm:w-auto' : 'btn btn-primary w-full sm:w-auto'}
        aria-label={
          isOverdue
            ? `Complete overdue inspection for ${row.vehicle_number}`
            : `Complete monthly inspection for ${row.vehicle_number}`
        }
      >
        <ClipboardCheck className="w-4 h-4 mr-2" />
        {isOverdue ? 'Complete Overdue Inspection' : 'Complete Monthly Inspection'}
      </button>
    </div>
  );
};

const MyInspectionsDue = () => {
  const { data: rows = [] } = useMyInspectionsDue();
  const submitInspection = useSubmitVehicleInspection();
  const [inspectRow, setInspectRow] = useState(null);

  const actionable = rows.filter(
    (r) => r.row_status === 'due' || r.row_status === 'overdue'
  );

  if (actionable.length === 0) return null;

  const overdueCount = actionable.filter((r) => r.row_status === 'overdue').length;
  const borderTone = overdueCount > 0 ? 'border-red-200' : 'border-amber-200';
  const iconTone = overdueCount > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';

  return (
    <div className={`bg-white rounded-2xl shadow-sm border ${borderTone} p-4 sm:p-6`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${iconTone}`}>
          <ClipboardCheck className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900">
            Monthly Vehicle Inspection ({actionable.length})
          </h2>
          <p className="text-xs text-gray-500">
            {overdueCount > 0
              ? `${overdueCount} overdue — the system tracks your last inspection and will keep reminding you.`
              : 'Required once per calendar month. We track when you last submitted — no date to enter.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {actionable.map((row) => (
          <InspectionDueCard
            key={row.asset_id}
            row={row}
            onInspect={setInspectRow}
          />
        ))}
      </div>

      <InspectVehicleModal
        isOpen={!!inspectRow}
        onClose={() => setInspectRow(null)}
        vehicle={inspectRow}
        isLoading={submitInspection.isPending}
        onSubmit={(data) => {
          if (!inspectRow?.asset_id) return;
          submitInspection.mutate(
            { assetId: inspectRow.asset_id, ...data },
            { onSuccess: () => setInspectRow(null) },
          );
        }}
      />
    </div>
  );
};

export default MyInspectionsDue;
